import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Space } from 'antd';
import {
  SettingOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  CreditCardOutlined,
  BarChartOutlined,
  ExportOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { ROUTES } from '../../config/routes';
import { usePermission } from '../../hooks/usePermission';
import { COLORS, GRADIENTS } from '../../constants/colors';

const { Title, Text } = Typography;

interface SettingCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  onClick: () => void;
}

const SettingCard: React.FC<SettingCardProps> = ({ title, description, icon, onClick }) => (
  <Card
    hoverable
    onClick={onClick}
    style={{
      cursor: 'pointer',
      height: '100%',
      transition: 'all 0.3s ease',
    }}
    bodyStyle={{
      padding: 24,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: GRADIENTS.settings,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          color: COLORS.white,
        }}
      >
        {icon}
      </div>
      <div>
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
          {title}
        </Title>
        <Text type="secondary">{description}</Text>
      </div>
    </Space>
  </Card>
);

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isOwner } = usePermission();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  // 基础设置项（所有有权限访问设置页面的用户都能看到）
  const baseSettings: SettingCardProps[] = [
    {
      title: '租户设置',
      description: '管理公司信息、行业类型、联系方式等基本信息',
      icon: <SettingOutlined />,
      path: ROUTES.SETTINGS_TENANT,
      onClick: () => handleNavigate(ROUTES.SETTINGS_TENANT),
    },
  ];

  // 管理员专属设置项
  const adminSettings: SettingCardProps[] = [
    {
      title: '成员管理',
      description: '邀请、管理团队成员，分配角色权限',
      icon: <TeamOutlined />,
      path: ROUTES.SETTINGS_MEMBERS,
      onClick: () => handleNavigate(ROUTES.SETTINGS_MEMBERS),
    },
    {
      title: '角色权限',
      description: '配置角色权限，控制功能访问范围',
      icon: <SafetyCertificateOutlined />,
      path: ROUTES.SETTINGS_ROLES,
      onClick: () => handleNavigate(ROUTES.SETTINGS_ROLES),
    },
    {
      title: '用量统计',
      description: '查看系统使用情况、资源消耗统计',
      icon: <BarChartOutlined />,
      path: ROUTES.SETTINGS_USAGE,
      onClick: () => handleNavigate(ROUTES.SETTINGS_USAGE),
    },
    {
      title: '数据导出',
      description: '导出业务数据，支持多种格式',
      icon: <ExportOutlined />,
      path: ROUTES.SETTINGS_DATA_EXPORT,
      onClick: () => handleNavigate(ROUTES.SETTINGS_DATA_EXPORT),
    },
  ];

  // 所有者专属设置项
  const ownerSettings: SettingCardProps[] = [
    {
      title: '订阅管理',
      description: '管理套餐订阅、查看账单、升级服务',
      icon: <CreditCardOutlined />,
      path: ROUTES.SETTINGS_SUBSCRIPTION,
      onClick: () => handleNavigate(ROUTES.SETTINGS_SUBSCRIPTION),
    },
    {
      title: '账户管理',
      description: '管理账户状态、冻结或删除账户',
      icon: <UserOutlined />,
      path: ROUTES.SETTINGS_ACCOUNT_LIFECYCLE,
      onClick: () => handleNavigate(ROUTES.SETTINGS_ACCOUNT_LIFECYCLE),
    },
  ];

  // 根据权限组合设置项
  let allSettings = [...baseSettings];
  if (isAdmin) {
    allSettings = [...allSettings, ...adminSettings];
  }
  if (isOwner) {
    allSettings = [...allSettings, ...ownerSettings];
  }

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <Title level={4} style={{ margin: 0, marginBottom: 8 }}>
          系统设置
        </Title>
        <Text type="secondary">
          管理您的账户、团队成员和系统配置
        </Text>
      </div>

      <Row gutter={[24, 24]}>
        {allSettings.map((setting) => (
          <Col xs={24} sm={12} lg={8} key={setting.path}>
            <SettingCard {...setting} />
          </Col>
        ))}
      </Row>
    </div>
  );
};
